/**
 * Communication Diary API Service
 *
 * The shared day-by-day record between a child's care team and their home. One page
 * per child per day, grouped into volumes by the IEP version in force on the date.
 *
 * Every write returns the WHOLE diary rather than the row it touched, so the screen
 * never has to reconcile a partial response against what it already holds — a note
 * and a home activity can both create the same day page, and the server is the only
 * thing that knows whether it did.
 */

import { get, put, post, patch, del } from './client';

/**
 * @param {string} clientId
 * @param {string} [iepVersionId] limit to one volume
 * @returns {Promise<{days: Array, volumes: Array, can: object}>} `can` is what the
 *   SIGNED-IN user may write — resolved server-side so the screen never infers
 *   permission from a role name.
 */
export const getDiary = (clientId, iepVersionId) =>
  get(`/communication-diary/${clientId}`, iepVersionId ? { iep_version_id: iepVersionId } : {});

/** Record the session slot and what was worked on for one date. */
export const saveDiaryDay = (clientId, data) => put(`/communication-diary/${clientId}/day`, data);

/** Write, or rewrite, the caller's own note. The voice comes from the caller. */
export const saveDiaryNote = (clientId, data) => put(`/communication-diary/${clientId}/note`, data);

/** Write back on a caregiver's question, as a thread — from the other side. `data`: { body }. */
export const replyToNote = (clientId, noteId, data) => post(`/communication-diary/${clientId}/note/${noteId}/reply`, data);

/** Resolve a caregiver's question (clear its awaiting-reply flag) from the other side. */
export const resolveNote = (clientId, noteId) => post(`/communication-diary/${clientId}/note/${noteId}/resolve`, {});

export const addHomeActivity = (clientId, data) => post(`/communication-diary/${clientId}/home-activity`, data);

/** The child's active-IEP targets a therapist/supervisor may assign for home, or record
 *  as worked on. */
export const getAssignableTargets = (clientId) => get(`/communication-diary/${clientId}/assignable-targets`);

/** Assign one or more targets to a page as home activities. `data`: { entry_date, target_ids }. */
export const addTargetActivities = (clientId, data) => post(`/communication-diary/${clientId}/target-activity`, data);

export const tickHomeActivity = (clientId, activityId, isDone) =>
  patch(`/communication-diary/${clientId}/home-activity/${activityId}`, { is_done: isDone });

export const deleteHomeActivity = (clientId, activityId) => del(`/communication-diary/${clientId}/home-activity/${activityId}`);

/** Raise a concern on a page. `data`: { entry_date, concern_type, severity, body, action_taken, response_needed }. */
export const reportConcern = (clientId, data) => post(`/communication-diary/${clientId}/concern`, data);

/** Write back on an open concern from the other side of the diary. `data`: { body }. */
export const replyToConcern = (clientId, concernId, data) => post(`/communication-diary/${clientId}/concern/${concernId}/reply`, data);

/** Mark a concern closed (clinician only). */
export const closeConcern = (clientId, concernId) => post(`/communication-diary/${clientId}/concern/${concernId}/close`, {});
